import structuredClone from '@ungap/structured-clone';
import { getField, updateField } from 'vuex-map-fields';
import {
  pathOr, pick, without,
} from 'ramda';
import { format } from 'date-fns';
import { MSG_INVOICE_CREATING, MSG_INVOICE_CREATE_ERROR } from '~/config/flashMessagesConstants';
import isForcedEetAtPayment from '~/lib/validators/isForcedEetAtPayment';
import uuid from 'uuid-random';

import {
  COUNTRY_CODES,
} from '~/config/constants';

import i18n from '~/i18n';

/* eslint no-underscore-dangle: "off" */

import {
  getFormVisibilities,
  getFieldsDisabled,
  getDefaultValues,
  getDefaultValue,
  getInvoiceFormConfig,
  getFormValidations,
} from '~/config/formConfig';

import {
  buyerAndSellerCountryIsEq,
} from '~/lib/formConfigHelpers';

import {
  mapInvoiceDataToForm, replaceEmptyDataWithDefault, checkAttachments, isDefaultLine, removeIds,
} from './helpers';

const ROOT_KEYS = [
  'color',
  'constant_symbol',
  'conversion_rate_message',
  'credit_noted_invoice_id',
  'currency',
  'design',
  'due_in',
  'due_on_custom',
  'foot_note',
  'issued_by',
  'kind',
  'language',
  'logo',
  'means_of_payment',
  'note',
  'number',
  'payment_symbol',
  'specific_symbol',
  'reference',
  'registration_number',
  'rounding_type',
  'settlement_method',
  'show_qr_code',
  'stamp',
  'vat_calculation',
  'vat_totals_currency_conversion',
];

const PARTNER_KEYS = ['id', 'company_number', 'name', 'tax_number', 'vat_number'];
const ADDRESS_KEYS = ['street', 'city', 'postcode', 'country_code', 'subdivision'];
const BANK_KEYS = ['number', 'show_iban', 'iban', 'swift', 'sort_code', 'routing_number'];
const CONTACT_KEYS = ['email', 'phone', 'web'];

export const defaultValues = getDefaultValues('invoiceForm');

// root state object.
export const defaultState = () => structuredClone({
  buildComplete: false,
  buyer_address_city: defaultValues.buyer_address_city,
  buyer_address_country_code: defaultValues.buyer_address_country_code,
  buyer_address_subdivision: defaultValues.buyer_address_subdivision,
  buyer_address_postcode: defaultValues.buyer_address_postcode,
  buyer_address_street: defaultValues.buyer_address_street,
  buyer_company_name: '',
  buyer_company_number: defaultValues.buyer_company_number,
  buyer_email: defaultValues.buyer_email,
  buyer_history_list: defaultValues.buyer_history_list,
  buyer_id: null,
  buyer_name: defaultValues.buyer_name,
  buyer_phone: defaultValues.buyer_phone,
  buyer_tax_number: defaultValues.buyer_tax_number,
  buyer_vat_number: defaultValues.buyer_vat_number,
  buyer_web: defaultValues.buyer_web,
  color: defaultValues.color,
  constant_symbol: defaultValues.constant_symbol,
  conversion_rate_message: defaultValues.conversion_rate_message,
  currency: defaultValues.currency,
  delivery_on: defaultValues.delivery_on,
  design: defaultValues.design,
  dialogOpened: false,
  due_in: defaultValues.due_in,
  due_on_custom: defaultValues.due_on_custom,
  eet_invoice: defaultValues.eet_invoice,
  eet: {},
  fieldsDisabled: {},
  foot_note: defaultValues.foot_note,
  id: false,
  issued_by: defaultValues.issued_by,
  issued_on: defaultValues.issued_on,
  kind: defaultValues.kind,
  language: defaultValues.language,
  lines: defaultValues.lines,
  logo: defaultValues.logo,
  means_of_payment: defaultValues.means_of_payment,
  note: defaultValues.note,
  number: defaultValues.number,
  payment_symbol: defaultValues.payment_symbol,
  specific_symbol: defaultValues.specific_symbol,
  recurring_auto_submit: false,
  recurring_draft: false,
  recurring_enabled: false,
  recurring_end_options: 'never',
  recurring_ends_after_count: null,
  recurring_ends_on: null,
  recurring_frequency: 'monthly',
  recurring_issue_last_day: false,
  recurring_profile: false,
  recurring_submit_copy: false,
  reference: defaultValues.reference,
  register_eet: defaultValues.register_eet,
  registration_number: defaultValues.registration_number,
  remove_logo: false,
  remove_stamp: false,
  rounding_type: defaultValues.rounding_type,
  seller_address_city: defaultValues.seller_address_city,
  seller_address_country_code: defaultValues.seller_address_country_code,
  seller_address_subdivision: defaultValues.seller_address_subdivision,
  seller_address_postcode: defaultValues.seller_address_postcode,
  seller_address_street: defaultValues.seller_address_street,
  seller_bank_account_iban: defaultValues.seller_bank_account_iban,
  seller_bank_account_number: defaultValues.seller_bank_account_number,
  seller_bank_account_routing_number: defaultValues.seller_bank_account_routing_number,
  seller_bank_account_show_iban: defaultValues.seller_bank_account_show_iban,
  seller_bank_account_sort_code: defaultValues.seller_bank_account_sort_code,
  seller_bank_account_swift: defaultValues.seller_bank_account_swift,
  seller_company_number: defaultValues.seller_company_number,
  seller_email: defaultValues.seller_email,
  seller_id: null,
  seller_name: defaultValues.seller_name,
  seller_phone: defaultValues.seller_phone,
  seller_tax_number: defaultValues.seller_tax_number,
  seller_vat_number: defaultValues.seller_vat_number,
  seller_web: defaultValues.seller_web,
  settlement_method: null,
  show_qr_code: defaultValues.show_qr_code,
  stamp: defaultValues.stamp,
  state: null,
  tax_point_on: defaultValues.tax_point_on,
  vat_calculation: defaultValues.vat_calculation,
  vat_totals_currency_conversion: defaultValues.vat_totals_currency_conversion,
  visibilities: {},
  phoneValidations: {
    seller: null,
    buyer: null,
  },
});

const mutations = {
  updateField,
  setSequences(state, number) {
    state.number = number;
    state.registration_number = number;
    state.payment_symbol = number.replace(/\D/g, '');
  },
  checkLinesForLanguage(state) {
    if (isDefaultLine(state.lines[0], defaultValues.lines[0], ['id', 'unit_type'])) {
      state.lines[0].unit_type = i18n.t('invoice.unit', state.language);
    }
  },
  openPremiumDialog(state, type) {
    state.dialogOpened = type;
  },
  closePremiumDialog(state) {
    state.dialogOpened = false;
  },
  resetState(state) {
    Object.assign(state, defaultState());
  },
  updatePhoneValidations(state, data) {
    state.phoneValidations[data.name] = data.value;
  },
  updateStoreValue(state, data) {
    state[data.name] = data.value;
  },
  updateStoreValues(state, data) {
    data.forEach(({ name, value }) => {
      state[name] = value;
    });
  },
  setContactData: (state, action) => {
    const { data } = action.payload;

    Object.keys(data).forEach((key) => {
      if (key === 'id') {
        return;
      }

      if (['street', 'city', 'postcode', 'country_code', 'subdivision'].includes(key)) {
        if (data[key] !== null) {
          state[`buyer_address_${key}`] = data[key];
        }
      } else {
        state[`buyer_${key}`] = data[key];
      }
    });
  },
  addInvoiceItem(state, discountData = {}) {
    const lines = state.lines.filter(line => line._destroy === false);
    const lastLine = lines[lines.length - 1];

    state.lines = [
      ...state.lines, {
        identifier: uuid(),
        quantity: 1,
        unit_type: lastLine?.unit_type || defaultValues.lines[0].unit_type,
        description: '',
        vat_rate: lastLine?.vat_rate || defaultValues.lines[0].vat_rate,
        price: 0,
        discount_amount: 0,
        discount_kind: 'percentage',
        is_discount_line: null,
        ...discountData,
        _destroy: false,
      },
    ];
  },
  applyDiscount(state, { index, discountData, isDiscountLine }) {
    const lineToUpdate = state.lines[index];
    state.lines.splice(index, 1, {
      ...lineToUpdate,
      discount_amount: discountData.discount_amount,
      discount_kind: discountData.discount_kind,
      is_discount_line: isDiscountLine,
    });
  },
  duplicateInvoiceItem(state, index) {
    const newItem = {
      ...state.lines[index],
      identifier: uuid(),
      position: state.lines.length,
    };
    if (newItem.id) {
      delete newItem.id;
    }
    state.lines.push(newItem);
  },
  addSavedInvoiceItem(state, payload) {
    const {
      description,
      discountAmount = 0,
      discountKind = 'percentage',
      isDiscountLine = null,
      price,
      quantity = 1,
      unitType,
      vatRate,
    } = payload;

    let alreadyAdded = false;
    const unchangedLine = defaultValues.lines[0];
    const newLine = {
      quantity,
      unit_type: unitType,
      description,
      vat_rate: vatRate,
      discount_amount: discountAmount,
      discount_kind: discountKind,
      is_discount_line: isDiscountLine,
      price,
      _destroy: false,
    };

    for (let i = 0; i < state.lines.length; i += 1) {
      if (isDefaultLine(state.lines[i], unchangedLine, ['id', 'identifier', 'unit_type', 'position'])) {
        Object.assign(state.lines[i], newLine);
        alreadyAdded = true;
        break;
      }
    }

    if (!alreadyAdded) {
      state.lines.push(newLine);
    }
  },
  removeEmptyLines: (state) => {
    const lines = state.lines.filter(line => line._destroy === false);
    const lastLine = lines[lines.length - 1];

    if (lastLine?.description === '' && lastLine?.price === 0 && lastLine?.is_discount_line === null) {
      lastLine._destroy = true;
    }
  },
  removeInvoiceItem: (state, index) => {
    const line = state.lines[index];
    line._destroy = true;
  },
  setZeroVatRates: (state) => {
    const newLines = state.lines.map(l => ({
      ...l,
      vat_rate: 0,
    }));
    state.lines = newLines;
  },
  computeVisibilities(state, { rootState }) {
    state.visibilities = getFormVisibilities(rootState, 'invoiceForm');
  },
  computeFieldsDisabled(state, { rootState }) {
    state.fieldsDisabled = getFieldsDisabled(rootState, 'invoiceForm');
  },
  getDefaultValue: (state, { fieldName }) => {
    state[fieldName] = getDefaultValue(state, 'invoiceForm', fieldName);
  },
  setCompanyData: (state, action) => {
    const { partyType } = action.payload.meta;
    if (state[`${partyType}_bank_account_number`] === '') {
      state[`${partyType}_bank_account_number`] = action.payload.data.bank_account;
    }
    state[`${partyType}_address_city`] = action.payload.data.address_city;
    state[`${partyType}_address_postcode`] = action.payload.data.address_postcode;
    state[`${partyType}_address_street`] = action.payload.data.address_street;
    state[`${partyType}_company_number`] = action.payload.data.reg_no;
    state[`${partyType}_name`] = action.payload.data.name;
    state[`${partyType}_tax_number`] = action.payload.data.vat_no;
    state[`${partyType}_vat_number`] = action.payload.data.tax_no;
  },
  setInvoiceData: (state, action) => {
    const data = pathOr(action.payload.data, ['payload', 'data', 'invoice'], action);
    Object.assign(state, replaceEmptyDataWithDefault(state, mapInvoiceDataToForm(data)));
    state.register_eet = isForcedEetAtPayment(data.means_of_payment);
    state['should_be_registered?'] = data['should_be_registered?'];
    state['locked?'] = data['locked?'];
    state.buildComplete = true;
  },
  updateLinesPositions(state, data) {
    const newLines = [...state.lines];
    data.forEach((item) => {
      const currentLine = newLines.find(line => line.identifier === item.identifier);
      if (currentLine) currentLine.position = item.position;
    });
    state.lines = newLines;
  },
  removeLineDiscount(state, index) {
    const line = state.lines[index];
    Object.assign(line, {
      discount_amount: 0,
      discount_kind: 'percentage',
      is_discount_line: null,
    });
  },
  setDefaultKind(state) {
    state.kind = defaultValues.kind;
  },
};

const actions = {
  setDefaultKind({ commit }) {
    commit('setDefaultKind');
  },
  setSequences({ commit }, payload) {
    commit('setSequences', payload.nextInvoiceNumber);
  },
  checkLinesForLanguage: ({ commit }) => {
    commit('checkLinesForLanguage');
  },
  closePremiumDialog: ({ commit }) => {
    commit('closePremiumDialog');
  },
  openPremiumDialog: ({ commit }, kind) => {
    commit('openPremiumDialog', kind);
  },
  openPremiumDialogDownload: ({ commit }) => {
    commit('openPremiumDialog', 'download');
  },
  addInvoiceItem: ({ commit }) => {
    commit('addInvoiceItem');
  },
  duplicateInvoiceItem({ commit }, index) {
    commit('duplicateInvoiceItem', index);
  },
  addSavedInvoiceItem: ({ commit, dispatch }, data) => new Promise((resolve) => {
    commit('addSavedInvoiceItem', data);
    dispatch('analytics/trackEvent', {
      analytics: {
        action: 'item',
        category: 'savedLines',
        label: 'addToInvoice',
      },
    }, { root: true });
    resolve();
  }),
  addInvoiceItemWithData: ({ commit }, data) => {
    const transformedData = {
      description: data.description,
      discountAmount: data.discount_amount,
      discountKind: data.discount_kind,
      isDiscountLine: data.is_discount_line,
      price: parseInt(data.price, 10),
      quantity: parseInt(data.quantity, 10),
      unitType: data.unit_type,
      vatRate: parseInt(data.vat_rate, 10),
    };

    commit('addSavedInvoiceItem', transformedData);
  },
  updateStoreValue: ({ commit }, data) => {
    commit('updateStoreValue', data);
  },
  updateStoreValues: ({ commit }, data) => {
    commit('updateStoreValues', data);
  },
  updatePhoneValidations: ({ commit }, data) => {
    commit('updatePhoneValidations', data);
  },
  computeVisibilities: ({ commit, rootState }) => {
    commit('computeVisibilities', { rootState });
  },
  computeFieldsDisabled: ({ commit, rootState }) => {
    commit('computeFieldsDisabled', { rootState });
  },
  getDefaultValue: ({ commit }, { fieldName }) => {
    commit('getDefaultValue', { fieldName });
  },
  fetchContactData({ commit, rootGetters }, id) {
    commit(
      'api/fetch',
      {
        url: `/contacts/${id}`,
        method: 'GET',
        headers: rootGetters['auth/getHeaders'],
        onSuccess: ['invoiceForm/setContactData'],
      },
      { root: true },
    );
  },
  setContactData({ commit }, payload) {
    commit('setContactData', payload);
  },
  setCompanyData({ commit }, payload) {
    commit('setCompanyData', payload);
  },
  setCompanyDataLoading({ commit }, payload) {
    commit('setCompanyDataLoading', payload);
  },
  setInvoiceData({ commit }, payload) {
    commit('setInvoiceData', payload);
  },
  previewInvoice({ commit, getters, rootGetters }, options) {
    const dataWithoutIds = removeIds(getters.invoiceApiData);

    commit(
      'api/fetch',
      {
        url: '/invoices.pdf',
        method: 'POST',
        responseType: 'blob',
        body: {
          invoice: dataWithoutIds,
          preview: true,
        },
        headers: rootGetters['auth/getHeaders'],
        onSuccess: [
          'invoiceForm/setInvoiceData',
          'invoiceForm/createInvoiceSuccess',
          {
            type: 'analytics/trackEvent',
            analytics: {
              action: 'new',
              category: 'invoice',
              label: 'preview',
            },
          },
        ],
        options,
      },
      { root: true },
    );
  },
  createInvoice({
    commit, getters, rootGetters, dispatch,
  }, options) {
    return new Promise((resolve, reject) => {
      dispatch('removeEmptyLines', { root: true });
      dispatch(
        'uiEffects/scrollTo',
        {
          top: 0,
          left: 0,
        },
        { root: true },
      );

      dispatch('flashMessages/addMessage', MSG_INVOICE_CREATING, { root: true });

      const submittedAt = options.action === 'saveAndDownload' && getters.shouldSetSubmittedAt ? { submitted_at: new Date() } : {};

      commit(
        'api/fetch',
        {
          url: getters.invoiceApiUrl,
          method: getters.invoiceApiMethod,
          body: {
            invoice: {
              ...getters.invoiceApiData,
              ...submittedAt,
            },
          },
          headers: rootGetters['auth/getHeaders'],
          promise: {
            resolve,
            reject,
          },
          onSuccess: ['invoiceForm/setInvoiceData', 'invoiceForm/createInvoiceSuccess',
            {
              type: 'analytics/trackEvent',
              analytics: {
                action: 'new',
                category: 'invoice',
                label: options.action,
              },
            },
          ],
          onError: ['invoiceForm/createInvoiceError'],
          options,
        },
        { root: true },
      );
    });
  },
  buildInvoice({ commit, rootGetters }, { invoiceId, type }) {
    commit(
      'api/fetch',
      {
        url: `/invoices/${invoiceId}/${type}`,
        method: 'GET',
        headers: rootGetters['auth/getHeaders'],
        onSuccess: ['invoiceForm/setInvoiceData'],
        onError: [],
      },
      { root: true },
    );
  },
  resetState: ({ commit, dispatch }) => {
    commit('resetState');
    dispatch('computeVisibilities');
    dispatch('computeFieldsDisabled');
  },
  removeEmptyLines: ({ commit }) => {
    commit('removeEmptyLines');
  },
  createInvoiceSuccess({ dispatch }, action) {
    const { options } = action.payload.meta;

    if (options.action === 'preview') {
      dispatch(
        {
          type: 'invoices/invoicePreview',
          blob: action.payload.data,
        },
        { root: true },
      );
    }

    if (options.action === 'saveAndDownload' || options.action === 'download') {
      const onSuccess = ['file/downloadFile'];

      dispatch('downloadInvoice', {
        id: action.payload.data.invoice_id,
        onSuccess,
      });
    }

    dispatch(
      'flashMessages/addMessage',
      {
        key: 'invoiceCreateSuccess',
        kind: 'success',
        params: {
          invoice_number: action.payload.data.invoice.number,
        },
      },
      { root: true },
    );
  },
  downloadInvoice({ dispatch }, option) {
    dispatch('removeEmptyLines', { root: true });

    dispatch(
      {
        type: 'invoices/invoiceDownload',
        ...option,
      },
      { root: true },
    );
  },
  createInvoiceError({ dispatch }) {
    dispatch('flashMessages/addMessage', MSG_INVOICE_CREATE_ERROR, { root: true });
  },
  updateLinesPositions({ commit }, payload) {
    commit('updateLinesPositions', payload);
  },
  removeInvoiceDiscount({ commit, getters, state }) {
    const { InvoiceWideDiscountLine } = getters;

    if (InvoiceWideDiscountLine) {
      const lineIndex = state.lines.findIndex(line => line === InvoiceWideDiscountLine);
      if (lineIndex !== -1) {
        commit('removeInvoiceItem', lineIndex);
      }
    }
  },
  removeLineDiscount({ commit }, index) {
    commit('removeLineDiscount', index);
  },
};

/* eslint-disable no-shadow */
const getters = {
  getField,
  validationRules: state => getFormValidations(getInvoiceFormConfig(state)),
  buildComplete: state => state.buildComplete,
  invoice: state => state,
  invoiceId: state => state.id,
  shouldSetSubmittedAt: state => state.state === 'proposal' || state.state === null,
  visibilities: state => state.visibilities,
  fieldsDisabled: state => state.fieldsDisabled,
  kindOptions: state => state.kindOptions,
  meansOfPaymentOptions: state => state.meansOfPaymentOptions,
  linesWithoutDeleted: state => state.lines.filter(item => item._destroy !== true),
  sortedLines: state => state.lines.sort((a, b) => a.position - b.position),
  bankApiData: state => (prefix) => {
    const response = {};

    const useIban = state[`${prefix}_bank_account_show_iban`] === true;

    BANK_KEYS.forEach((key) => {
      const responseKey = key === 'number' ? 'bank_account_number' : key;

      response[responseKey] = state[`${prefix}_bank_account_${key}`] ?? null;

      if (useIban === false && ['iban', 'swift'].some(item => item === key)) {
        response[responseKey] = null;
      }

      if (!buyerAndSellerCountryIsEq(state, COUNTRY_CODES.EN)) {
        response.routing_number = null;
      }

      if (!buyerAndSellerCountryIsEq(state, COUNTRY_CODES.GB)) {
        response.sort_code = null;
      }
    });
    return response;
  },

  contactData: state => (prefix) => {
    const response = {};
    CONTACT_KEYS.forEach((key) => {
      response[key] = state[`${prefix}_${key}`] || null;
    });
    return response;
  },

  addressApiData: state => (prefix) => {
    const response = {};
    ADDRESS_KEYS.forEach((key) => {
      response[key] = state[`${prefix}_address_${key}`] || null;
    });
    return response;
  },

  partnerApiData: (state, getters) => (prefix) => {
    const response = {};
    PARTNER_KEYS.forEach((key) => {
      if (key === 'vat_number' && state[`${prefix}_address_country_code`] !== COUNTRY_CODES.SK) {
        response[key] = null;
        return;
      }
      response[key] = state[`${prefix}_${key}`] || null;
    });

    return {
      ...response,
      ...getters.addressApiData(prefix),
      ...getters.bankApiData(prefix),
      ...getters.contactData(prefix),
    };
  },

  recurringApiData: (state) => {
    if (state.recurring_enabled === true || state.recurring_profile === true) {
      return {
        recurring_profile_attributes: {
          end_options: state.recurring_end_options,
          frequency: state.recurring_frequency,
          ends_on: state.recurring_ends_on,
          ends_after_count: state.recurring_ends_after_count,
          issue_last_day: state.recurring_issue_last_day,
          auto_submit: state.recurring_auto_submit,
          submit_copy: state.recurring_submit_copy,
        },
      };
    }
    return {};
  },
  dueOnApiData: state => (state.due_in === 'custom'
    ? { due_on_localized: format(state.due_on_custom, 'dd. MM. yyyy') }
    : { due_in: state.due_in }),

  deliveryOnData: state => (state?.delivery_on !== null ? { delivery_on: format(state.delivery_on, 'dd. MM. yyyy') } : {}),

  getStamp: state => checkAttachments(state, 'stamp'),
  getStampUrl: state => state?.stamp[0]?.url,

  getLogo: state => checkAttachments(state, 'logo'),
  getLogoUrl: state => state?.logo[0]?.url,

  invoiceAttachments: (state, getters) => {
    const logo = state.remove_logo
      ? { remove_logo_id: state.remove_logo }
      : { ...getters.getLogo };
    const stamp = state.remove_stamp
      ? { remove_stamp_id: state.remove_stamp }
      : { ...getters.getStamp };

    return {
      ...stamp,
      ...logo,
    };
  },

  vatTotalCurrency: state => (state.vat_totals_currency_conversion ? 'CZK' : ''),

  invoiceApiUrl(state) {
    return state.id ? `/invoices/${state.id}` : '/invoices';
  },
  shouldBeRegistered(state) {
    return pathOr(null, ['eet', 'should_be_registered?'], state);
  },
  isLocked(state) {
    return pathOr(null, ['eet', 'locked?'], state);
  },
  invoiceApiMethod: state => (state.id ? 'PUT' : 'POST'),
  invoiceApiData: (state, getters, rootState) => {
    if (!state) return {};
    const registerEet = rootState.config.subscription['eet_enabled?'] ? state.register_eet : false;
    return {
      ...pick(
        without(
          [
            'due_in',
            'due_on_custom',
            'conversion_rate_message',
            'logo',
            'stamp',
            'currency_vat_conversion',
          ],
          ROOT_KEYS,
        ),
        state,
      ),
      ...getters.deliveryOnData,
      ...getters.dueOnApiData,
      ...getters.invoiceAttachments,
      contact_id: state.buyer_company_name,
      vat_totals_currency: getters.vatTotalCurrency,
      issued_on: format(state.issued_on, 'dd. MM. yyyy'),
      tax_point_on: format(state.tax_point_on, 'dd. MM. yyyy'),
      // eslint-disable-next-line no-unused-vars
      lines_attributes: state.lines.map(({ identifier, ...line }) => line),
      buyer_attributes: getters.partnerApiData('buyer'),
      seller_attributes: getters.partnerApiData('seller'),
      ...getters.recurringApiData,
      register_eet: registerEet,
    };
  },
  InvoiceWideDiscountLine: state => state.lines.find(line => !line._destroy && line.is_discount_line === false),
};
/* eslint-enable no-shadow */

export default {
  namespaced: true,
  state: defaultState(),
  getters,
  actions,
  mutations,
};
